<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
const props = defineProps<{
  image: string;
  hotspots: Array<{
    x: string;
    y: string;
    title: string;
    description?: string;
    zoomThumbnail?: {
      mediaItemUrl: string;
    };
  }>;
}>();

const selectedHotspot = ref<number | null>(null);
const CARD_OFFSET = 20;
const EDGE_PADDING = 20;
let resizeTimer: number | null = null;

const convertedHotspots = computed(() => {
  return props.hotspots.map((hotspot) => {
    return {
      ...hotspot,
      xPercent: hotspot.x,
      yPercent: hotspot.y
    };
  });
});

const currentHotspot = computed(() => {
  if (selectedHotspot.value !== null) {
    return props.hotspots[selectedHotspot.value];
  }
  return null;
});

const toggleHotspot = (event: MouseEvent, index: number) => {
  event.stopPropagation();
  
  if (selectedHotspot.value === index) {
    selectedHotspot.value = null;
  } else {
    selectedHotspot.value = index;
    
    nextTick(() => {
      const marker = event.currentTarget as HTMLElement;
      const card = marker.querySelector('.hotspot__inner') as HTMLElement;
      if (card) {
        positionCard(marker, card);
      }
    });
  }
};

const positionCard = (marker: HTMLElement, card: HTMLElement) => {
  const container = marker.closest('.hotspot-image-container') as HTMLElement;
  if (!container) return;
  
  const containerRect = container.getBoundingClientRect();
  const markerRect = marker.getBoundingClientRect();
  
  card.style.visibility = 'hidden';
  card.style.display = 'block';
  card.style.position = 'fixed'; 
  const cardRect = card.getBoundingClientRect();
  card.style.position = ''; // Reset
  card.style.visibility = '';
  card.style.display = '';

  const cardWidth = cardRect.width;
  const cardHeight = cardRect.height;
  const markerCenterX = markerRect.left + markerRect.width / 2;
  const markerCenterY = markerRect.top + markerRect.height / 2;

  let left = 0;
  let top = 0;
  let positioning = 'right';

  const spaceRight = containerRect.right - markerCenterX;
  const spaceLeft = markerCenterX - containerRect.left;
  const spaceBelow = containerRect.bottom - markerCenterY;
  const spaceAbove = markerCenterY - containerRect.top;

  if (spaceRight >= cardWidth + CARD_OFFSET + EDGE_PADDING) {
    positioning = 'right';
    left = markerRect.width / 2 + CARD_OFFSET;
    top = -(cardHeight / 2);
  } else if (spaceLeft >= cardWidth + CARD_OFFSET + EDGE_PADDING) {
    positioning = 'left';
    left = -(cardWidth + CARD_OFFSET + markerRect.width / 2);
    top = -(cardHeight / 2);
  } else if (spaceBelow >= cardHeight + CARD_OFFSET + EDGE_PADDING) {
    positioning = 'below';
    left = -(cardWidth / 2);
    top = markerRect.height / 2 + CARD_OFFSET;
  } else if (spaceAbove >= cardHeight + CARD_OFFSET + EDGE_PADDING) {
    positioning = 'above';
    left = -(cardWidth / 2);
    top = -(cardHeight + CARD_OFFSET + markerRect.height / 2);
  } else {
    positioning = 'below-constrained';
    left = -(cardWidth / 2);
    top = markerRect.height / 2 + CARD_OFFSET;
  }

  if (positioning === 'right' || positioning === 'left') {
    const cardTop = markerCenterY + top;
    const cardBottom = cardTop + cardHeight;
    
    if (cardTop < containerRect.top + EDGE_PADDING) {
      top = containerRect.top + EDGE_PADDING - markerCenterY;
    } else if (cardBottom > containerRect.bottom - EDGE_PADDING) {
      top = containerRect.bottom - EDGE_PADDING - cardHeight - markerCenterY;
    }
  } else {
    const cardLeft = markerCenterX + left;
    const cardRight = cardLeft + cardWidth;
    
    if (cardLeft < containerRect.left + EDGE_PADDING) {
      left = containerRect.left + EDGE_PADDING - markerCenterX;
    } else if (cardRight > containerRect.right - EDGE_PADDING) {
      left = containerRect.right - EDGE_PADDING - cardWidth - markerCenterX;
    }
  }

  card.style.left = `${left}px`;
  card.style.top = `${top}px`;
};

const handleOutsideClick = (e: MouseEvent) => {
  if (!(e.target as HTMLElement).closest('.hotspot-marker')) {
    selectedHotspot.value = null;
  }
};

const handleEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    selectedHotspot.value = null;
  }
};

const handleResize = () => {
  if (resizeTimer) {
    clearTimeout(resizeTimer);
  }
  
  resizeTimer = window.setTimeout(() => {
    if (selectedHotspot.value !== null) {
      const markers = document.querySelectorAll('.hotspot-marker');
      const activeMarker = markers[selectedHotspot.value] as HTMLElement;
      if (activeMarker) {
        const card = activeMarker.querySelector('.hotspot__inner') as HTMLElement;
        if (card) {
          positionCard(activeMarker, card);
        }
      }
    }
  }, 100);
};

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
  document.addEventListener('keydown', handleEscape);
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleOutsideClick);
  document.removeEventListener('keydown', handleEscape);
  window.removeEventListener('resize', handleResize);
  if (resizeTimer) {
    clearTimeout(resizeTimer);
  }
});
</script>

<template>
  <div class="hotspot-viewer">
    <div class="hotspot-image-container">
      <NuxtImg
        v-if="image"
        :src="image"
        :alt="'Room view with hotspots'"
        class="main-image"
        loading="lazy"
        format="webp"
        :quality="90"
      />

      <!-- Hotspot markers -->
      <button
        v-for="(hotspot, index) in convertedHotspots"
        :key="index"
        class="hotspot-marker"
        :class="{ active: selectedHotspot === index }"
        :style="{
          left: `${hotspot.xPercent}%`,
          top: `${hotspot.yPercent}%`
        }"
        @click="toggleHotspot($event, index)"
        :aria-label="`View details for ${hotspot.title}`"
      >
        <span class="hotspot-pulse"></span>
        <span class="hotspot-dot"></span>
        <span class="hotspot__inner" :class="{ show: selectedHotspot === index }">
            <span class="btn__icon" v-if="hotspot?.zoomThumbnail">
              <NuxtImg
                :src="hotspot?.zoomThumbnail.mediaItemUrl"
                :alt="hotspot.title"
                loading="lazy"
                format="webp"
                :quality="85"
              />
            </span>
            <span class="btn__label"><span>{{ hotspot?.title }}</span></span>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.hotspot-viewer {
  position: relative;
  width: 100%;
  height: 100%;
}

.hotspot-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 30px;
  overflow: hidden;
}

.main-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 30px;
}

.hotspot-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 10;
  padding: 0;
}

.hotspot-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: white;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

.hotspot-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 50%;
  opacity: 0.6;
  animation: pulse 2s ease-in-out infinite;
  z-index: 1;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.8;
  }
  50% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.4;
  }
  100% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.8;
  }
}

.hotspot-marker:hover .hotspot-dot {
  transform: translate(-50%, -50%) scale(1.2);
  background: #f0f0f0;
}

/* POPUP CARD STYLES */
.hotspot__inner {
  position: absolute;
  display: none;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  z-index: 100;
  width: 180px;
  max-width: calc(100vw - 40px); /* Prevent overflow on small screens */
}

.hotspot__inner.show {
  display: block;
  animation: fadeInScale 0.3s ease forwards;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.btn__label {
  display: block;
  font-weight: 600;
  color: var(--bs-primary);    
  padding: 5px 12px;
  text-align: left;
  font-size: small;
  word-wrap: break-word;
}

.hotspot-description {
  display: block;
  padding: 12px 20px;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}

.btn__icon {
  display: block;    
  padding: 12px 12px 0px 12px;
}

.btn__icon img {
  width: 100%;
  height: 150px;
  border-radius: 8px;
  display: block;
  object-fit: cover;
}

.hotspot-marker.active {
  z-index: 20;
}

.hotspot-marker.active .hotspot-dot {
  background: #f0f0f0;
  transform: translate(-50%, -50%) scale(1.1);
}

.hotspot-marker.active .hotspot-pulse {
  animation: none;
  opacity: 0;
}

/* MOBILE OPTIMIZATIONS */
@media (max-width: 768px) {
  .hotspot__inner {
    width: 160px;
    max-width: calc(100vw - 40px);
  }
  
  .btn__icon img {
    height: 120px;
  }
  
  .btn__label {
    font-size: 0.75rem;
    padding: 4px 10px;
  }

  .hotspot-marker {
    width: 36px;
    height: 36px;
  }

  .hotspot-pulse {
    width: 36px;
    height: 36px;
  }
  
  .hotspot-dot {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 480px) {
  .hotspot__inner {
    width: 140px;
  }
  
  .btn__icon img {
    height: 100px;
  }
}
</style>